import { jsPDF } from 'jspdf';
import type { Receipt, ReceiptCategory, SpendSummary, TripRoute } from '../types';
import { configurePdfJsWorker } from './receiptAnalysis';
import { RECEIPT_CATEGORY_LABELS } from './categoryLabels';
import {
  compareReceiptsForSummary,
  formatReceiptSummaryLine,
  routeLegLabelsByReceiptId,
} from './receiptSummaryLines';
import { getReceiptBlob } from './receiptBlobStore';
import { compressRasterDataUrlForPdf } from './receiptImageCompress';

/** Safety cap for very large PDFs */
const MAX_PDF_PAGES_EXPORT = 50;

/** Exported receipt bundle must not exceed this size (product / email attachment limit). */
export const MAX_EXPORT_PDF_BYTES = 1024 * 1024;

const MM_TO_PX = 3.779527559;

export function safeFilePart(name: string): string {
  const s = name.replace(/[^a-zA-Z0-9-_]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  return (s || 'trip').slice(0, 48);
}

/** Keep jsPDF default Helvetica happy on most builds */
function sanitizePdfText(s: string): string {
  return s
    .replace(/\r\n/g, '\n')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\u20ac/g, 'EUR')
    // eslint-disable-next-line no-control-regex -- allow tab/newline + printable ASCII for jsPDF
    .replace(/[^\t\n\x20-\x7E]/g, '?');
}

function buildDoc(): { doc: jsPDF; margin: number; maxW: number; lineH: number; pageH: number } {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const margin = 14;
  const maxW = doc.internal.pageSize.getWidth() - margin * 2;
  const lineH = 5.2;
  const pageH = doc.internal.pageSize.getHeight();
  return { doc, margin, maxW, lineH, pageH };
}

function ensureY(
  doc: jsPDF,
  y: number,
  needed: number,
  margin: number,
  pageH: number
): number {
  if (y + needed <= pageH - margin) return y;
  doc.addPage();
  return margin;
}

function writeWrapped(
  doc: jsPDF,
  text: string,
  y: number,
  margin: number,
  maxW: number,
  lineH: number,
  pageH: number
): number {
  const lines = doc.splitTextToSize(sanitizePdfText(text), maxW);
  let cur = y;
  for (const line of lines) {
    cur = ensureY(doc, cur, lineH, margin, pageH);
    doc.text(line, margin, cur);
    cur += lineH;
  }
  return cur;
}

function dataUrlMime(dataUrl: string): string | null {
  const m = /^data:([^;,]+)/i.exec(dataUrl);
  return m ? m[1].toLowerCase().trim() : null;
}

function isPdfDataUrl(dataUrl: string): boolean {
  return dataUrlMime(dataUrl)?.includes('pdf') ?? false;
}

function isRasterImageDataUrl(dataUrl: string): boolean {
  const m = dataUrlMime(dataUrl);
  return Boolean(m?.startsWith('image/'));
}

function receiptLooksPdf(r: Receipt): boolean {
  if (r.mime_type === 'application/pdf') return true;
  if (r.file_name?.toLowerCase().endsWith('.pdf')) return true;
  if (r.image_url && isPdfDataUrl(r.image_url)) return true;
  return false;
}

function dataUrlToUint8Array(dataUrl: string): Uint8Array {
  const comma = dataUrl.indexOf(',');
  const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : '';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

function looksPdfReceipt(r: Receipt, blob: Blob): boolean {
  if (blob.type === 'application/pdf') return true;
  if (r.mime_type === 'application/pdf') return true;
  return Boolean(r.file_name?.toLowerCase().endsWith('.pdf'));
}

function looksImageReceipt(r: Receipt, blob: Blob): boolean {
  if (blob.type.startsWith('image/')) return true;
  if (r.mime_type?.startsWith('image/')) return true;
  return /\.(png|jpe?g|webp|gif|bmp|tif?f?)$/i.test(r.file_name ?? '');
}

/**
 * Append each page of a PDF as its own output page (full-page contain), preserving page count.
 */
async function appendPdfBytesAsSeparatePages(
  doc: jsPDF,
  bytes: Uint8Array,
  needsPageBreakBeforeFirst: boolean,
  jpegQuality: number,
  maxPageWidthPx: number
): Promise<number> {
  const pdfjs = await configurePdfJsWorker();

  const pdf = await pdfjs.getDocument({ data: bytes, useSystemFonts: true }).promise;
  const n = Math.min(pdf.numPages, MAX_PDF_PAGES_EXPORT);
  const pageWmm = doc.internal.pageSize.getWidth();
  const idealWpx = pageWmm * MM_TO_PX;
  const targetWpx = Math.min(idealWpx, maxPageWidthPx);

  let drawn = 0;
  for (let p = 1; p <= n; p++) {
    if (p === 1 && needsPageBreakBeforeFirst) {
      doc.addPage();
    } else if (p > 1) {
      doc.addPage();
    }

    const page = await pdf.getPage(p);
    const baseVp = page.getViewport({ scale: 1 });
    const scale = targetWpx / baseVp.width;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    await page.render({ canvasContext: ctx, viewport }).promise;

    const jpeg = canvas.toDataURL('image/jpeg', jpegQuality);
    addImageFullPage(doc, jpeg, 'JPEG');
    drawn++;
  }
  return drawn;
}

/** Scale image to fit entire page (contain), centered — no captions or margins */
function addImageFullPage(doc: jsPDF, dataUrl: string, format: 'JPEG' | 'PNG' | 'WEBP' | 'GIF'): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const props = doc.getImageProperties(dataUrl);
  const imgRatio = props.width / props.height;
  const pageRatio = pageW / pageH;
  let wMm: number;
  let hMm: number;
  if (imgRatio > pageRatio) {
    wMm = pageW;
    hMm = pageW / imgRatio;
  } else {
    hMm = pageH;
    wMm = pageH * imgRatio;
  }
  const x = (pageW - wMm) / 2;
  const y = (pageH - hMm) / 2;
  doc.addImage(dataUrl, format, x, y, wMm, hMm);
}

async function placeRasterFullPage(
  doc: jsPDF,
  dataUrl: string,
  maxLongSidePx: number,
  jpegQuality: number
): Promise<void> {
  const jpeg = await compressRasterDataUrlForPdf(dataUrl, maxLongSidePx, jpegQuality);
  addImageFullPage(doc, jpeg, 'JPEG');
}

export interface ReceiptsPdfBuildOptions {
  jpegQuality: number;
  /** Caps render width for PDF→raster pages and raster long-side scaling */
  maxPageWidthPx: number;
}

function rasterLongSidePx(maxPageWidthPx: number): number {
  return Math.max(maxPageWidthPx, Math.round(maxPageWidthPx * 1.45));
}

/**
 * One output page per image receipt; PDF receipts keep one output page per source page.
 * Originals from IndexedDB when present; otherwise legacy data URLs.
 */
async function buildReceiptsPdfBlob(
  receipts: Receipt[],
  opts: ReceiptsPdfBuildOptions
): Promise<{ blob: Blob; hasContent: boolean }> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let hasContent = false;
  const longSide = rasterLongSidePx(opts.maxPageWidthPx);

  for (const r of receipts) {
    try {
      const blob = await getReceiptBlob(r.id);
      if (blob && blob.size > 0) {
        if (looksPdfReceipt(r, blob)) {
          const n = await appendPdfBytesAsSeparatePages(
            doc,
            new Uint8Array(await blob.arrayBuffer()),
            hasContent,
            opts.jpegQuality,
            opts.maxPageWidthPx
          );
          if (n > 0) hasContent = true;
          continue;
        }
        if (looksImageReceipt(r, blob)) {
          const dataUrl = await blobToDataUrl(blob);
          if (hasContent) doc.addPage();
          await placeRasterFullPage(doc, dataUrl, longSide, opts.jpegQuality);
          hasContent = true;
          continue;
        }
      }

      if (!r.image_url) continue;
      const url = r.image_url;
      if (receiptLooksPdf(r) || isPdfDataUrl(url)) {
        const n = await appendPdfBytesAsSeparatePages(
          doc,
          dataUrlToUint8Array(url),
          hasContent,
          opts.jpegQuality,
          opts.maxPageWidthPx
        );
        if (n > 0) hasContent = true;
      } else if (isRasterImageDataUrl(url)) {
        if (hasContent) doc.addPage();
        await placeRasterFullPage(doc, url, longSide, opts.jpegQuality);
        hasContent = true;
      }
    } catch {
      /* skip failed receipt */
    }
  }

  if (!hasContent) return { blob: new Blob(), hasContent: false };

  const out = doc.output('blob');
  return { blob: out, hasContent: true };
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Receipt bundle PDF — max {@link MAX_EXPORT_PDF_BYTES} by recompressing JPEGs and lowering render resolution if needed.
 * Returns `null` if nothing could be embedded, or on failure after retries.
 */
export async function exportTripReceiptsPdfBlob(
  tripName: string,
  receipts: Receipt[]
): Promise<{ blob: Blob; filename: string } | null> {
  const doc0 = new jsPDF({ unit: 'mm', format: 'a4' });
  const fullWidthPx = Math.ceil(doc0.internal.pageSize.getWidth() * MM_TO_PX);

  let jpegQuality = 0.92;
  let maxPageWidthPx = fullWidthPx;
  const filename = `${safeFilePart(tripName)}-receipts.pdf`;

  for (let attempt = 0; attempt < 16; attempt++) {
    const { blob, hasContent } = await buildReceiptsPdfBlob(receipts, {
      jpegQuality,
      maxPageWidthPx,
    });

    if (!hasContent) return null;

    if (blob.size <= MAX_EXPORT_PDF_BYTES) {
      return { blob, filename };
    }

    if (jpegQuality > 0.5) {
      jpegQuality = Math.max(0.5, jpegQuality - 0.055);
    } else {
      maxPageWidthPx = Math.max(320, Math.floor(maxPageWidthPx * 0.86));
    }
  }

  window.alert(
    'Could not create a receipt PDF under 1 MB — too many pages or images are too large. Try exporting fewer receipts or use smaller scans.'
  );
  return null;
}

export async function downloadReceiptsPdf(tripName: string, receipts: Receipt[]): Promise<void> {
  const out = await exportTripReceiptsPdfBlob(tripName, receipts);
  if (out) downloadBlob(out.blob, out.filename);
}

export function buildSummaryPdfBlob(
  tripName: string,
  summary: SpendSummary,
  receipts: Receipt[],
  routes: TripRoute[]
): Blob {
  const { doc, margin, maxW, lineH, pageH } = buildDoc();
  let y = margin;

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  y = ensureY(doc, y, 10, margin, pageH);
  doc.text('Trip summary', margin, y);
  y += 10;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  y = writeWrapped(doc, `Trip: ${tripName}`, y, margin, maxW, lineH, pageH);
  y = writeWrapped(doc, `Generated: ${new Date().toISOString().slice(0, 19)}Z`, y, margin, maxW, lineH, pageH);
  y = writeWrapped(doc, `Receipts included in this summary: ${receipts.length}`, y, margin, maxW, lineH, pageH);
  y += 6;

  const legMap = routeLegLabelsByReceiptId(routes);
  const sortedReceipts = [...receipts].sort(compareReceiptsForSummary);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  y = ensureY(doc, y, lineH, margin, pageH);
  doc.text('By receipt', margin, y);
  y += lineH + 3;

  doc.setFontSize(10);
  if (sortedReceipts.length === 0) {
    doc.setFont('helvetica', 'normal');
    y = writeWrapped(doc, 'No receipts in this trip yet.', y, margin, maxW, lineH, pageH);
  } else {
    doc.setFont('helvetica', 'normal');
    for (const r of sortedReceipts) {
      const line = formatReceiptSummaryLine(r, legMap);
      y = writeWrapped(doc, line, y, margin, maxW, lineH, pageH);
    }
  }

  y += 6;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  y = ensureY(doc, y, lineH, margin, pageH);
  doc.text('Spend by category', margin, y);
  y += lineH + 3;

  doc.setFontSize(10);
  const cats = (Object.keys(summary.byCategory) as ReceiptCategory[]).filter(
    (k) => summary.byCategory[k] > 0
  );

  if (cats.length === 0) {
    doc.setFont('helvetica', 'normal');
    y = writeWrapped(doc, 'No receipt amounts yet.', y, margin, maxW, lineH, pageH);
  } else {
    for (const k of cats) {
      doc.setFont('helvetica', 'normal');
      const line = `${RECEIPT_CATEGORY_LABELS[k]}: ${summary.byCategory[k].toFixed(2)} ${summary.currency}`;
      y = writeWrapped(doc, line, y, margin, maxW, lineH, pageH);
    }
    y += 2;
    doc.setFont('helvetica', 'bold');
    y = ensureY(doc, y, lineH, margin, pageH);
    doc.text(`Total: ${summary.total.toFixed(2)} ${summary.currency}`, margin, y);
    y += lineH;
  }

  return doc.output('blob');
}

export function downloadSummaryPdf(
  tripName: string,
  summary: SpendSummary,
  receipts: Receipt[],
  routes: TripRoute[]
): void {
  const blob = buildSummaryPdfBlob(tripName, summary, receipts, routes);
  downloadBlob(blob, `${safeFilePart(tripName)}-summary.pdf`);
}
